import React from 'react';
import { Divider, Typography } from '@mui/material';
import {
  EditOutlined,
  EmailOutlined,
  Delete,
  LockReset,
} from '@mui/icons-material';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import PageFilters, { Filter } from 'components/new/PageFilters';
import StatusBadge from 'components/new/StatusBadge';
import { Column, Row } from 'components/Layout';
import SearchField from 'further-ui/components/SearchField';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import Avatar from 'further-ui/components/Avatar';
import EditUserDrawer from './EditUserDrawer';
import AddUserDrawer from './AddUserDrawer';
import { useUserManagementNavigationContext } from '../UserManagementNavigationContext';

const pageId = 'user-management';
const FURTHER_STAFF_BG_COLOR = '#E3F2FD';

const Users: React.FC = () => {
  const [canAddUser, canEdit, canDelete, canManageUserSecurity] =
    useHasPermissions([
      RolePermission.AddUserToFirm,
      RolePermission.EditUsers,
      RolePermission.RemoveUserFromFirm,
      RolePermission.ManageUserSecurity,
    ]);
  const removeUserDisclosure = useDisclosure<{ id: string }>();
  const resetMfaDisclosure = useDisclosure<{ id: string }>();
  const {
    editUserDrawerUserId,
    openEditUserDrawer,
    closeEditUserDrawer,
    isAddUserDrawerOpen,
    openAddUserDrawer,
    closeAddUserDrawer,
  } = useUserManagementNavigationContext();
  const { pagination, filters, handleChangeFilters } = usePaginatedFilters(
    pageId,
    {
      defaultFilters: { searchQuery: '' },
      defaultOrder: { orderBy: 'firstName', order: 'asc' },
      orderByOptions: ['firstName', 'lastName', 'email', 'createdAt'],
    },
  );

  const [users, { isFetching }] =
    api.adminUser.listCurrentFirmUsers.useSuspenseQuery({});

  const requestPasswordReset =
    api.adminUser.requestPasswordResetForUser.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Password reset email sent.',
        invalidations: [],
      }),
    );

  const resetMfa = api.adminUser.resetMfa.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'MFA reset successfully.',
      onSuccess: () => {
        resetMfaDisclosure.onClose();
      },
    }),
  );

  const deleteUser = api.adminUser.removeUserFromCurrentFirm.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User removed successfully.',
      onSuccess: () => {
        removeUserDisclosure.onClose();
      },
    }),
  );

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    if (!filters.searchQuery) return true;

    const searchLower = filters.searchQuery.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      user.roles?.some((role) => role.name.toLowerCase().includes(searchLower))
    );
  });

  const paginatedUsers = filteredUsers.slice(
    (pagination.page - 1) * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage,
  );

  const columns: Array<TableColumn<(typeof users)[number]>> = [
    {
      label: 'User',
      key: 'userNameAndEmail',
      render: (user) => (
        <Row centered spacing="xs">
          <Avatar
            text={`${user.firstName} ${user.lastName}`}
            src={user.profilePicUrl}
            size="lg"
            color={!user.profilePicUrl ? 'random' : undefined}
          />
          <Column>
            <Typography variant="inherit">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user.email}
            </Typography>
          </Column>
        </Row>
      ),
    },
    {
      label: 'Role',
      key: 'role',
      render: (user) => (
        <Typography variant="inherit">{user.roles?.[0]?.name}</Typography>
      ),
    },
    ...(canManageUserSecurity
      ? [
          {
            label: 'MFA enrolled',
            key: 'hasMfaEnrolled',
            render: (user) => (
              <StatusBadge color={user.hasMfaEnrolled ? 'success' : 'error'} />
            ),
          },
        ]
      : []),
    {
      label: 'Last login',
      key: 'lastLogin',
      render: (user) => (
        <Typography variant="inherit">
          {user.lastLogin ? user.lastLogin.toReadable() : 'Never'}
        </Typography>
      ),
    },
    {
      label: 'Date added',
      key: 'createdAt',
      render: (user) => (
        <Typography variant="inherit">{user.createdAt.toReadable()}</Typography>
      ),
    },
  ];

  return (
    <Column spacing="lg">
      <PageFilters
        actions={[
          {
            label: 'Add user',
            variant: 'contained',
            onClick: openAddUserDrawer,
            hidden: !canAddUser,
          },
        ]}
      >
        <Filter size="sm">
          <SearchField
            autoSearchOnDebounce
            debounceTime={100}
            name="searchQuery"
            placeholder="Search by user"
            fullWidth
            value={filters.searchQuery}
            onSearch={(searchQuery) => {
              handleChangeFilters({ searchQuery });
            }}
            allowEmptyQuery
          />
        </Filter>
      </PageFilters>
      <Divider />
      <Table
        columns={columns}
        rows={paginatedUsers}
        isLoading={isFetching || pagination.isPaginationChanging}
        rowsTotalCount={filteredUsers.length}
        disableSorting
        pagination={pagination}
        emptyMessage="No users found"
        getRowBackgroundColor={(user) =>
          user.isFurtherStaff ? FURTHER_STAFF_BG_COLOR : undefined
        }
        actions={[
          {
            label: 'Edit',
            icon: EditOutlined,
            onClick: (user) => {
              openEditUserDrawer(user.id);
            },
            hidden: !canEdit,
          },
          {
            label: 'Send password reset email',
            icon: EmailOutlined,
            onClick: (user) => {
              requestPasswordReset.mutate({ id: user.id });
            },
            disabled: requestPasswordReset.isPending,
            hidden: !canManageUserSecurity,
          },
          {
            label: 'Reset MFA',
            icon: LockReset,
            onClick: (user) => {
              resetMfaDisclosure.stageAction({ id: user.id });
            },
            hidden: (user) => !canManageUserSecurity || !user.hasMfaEnrolled,
          },
          {
            label: 'Delete user',
            icon: Delete,
            onClick: (user) => {
              removeUserDisclosure.stageAction({ id: user.id });
            },
            disabled: deleteUser.isPending,
            hidden: !canDelete,
          },
        ]}
      />
      <ConfirmDialog
        open={removeUserDisclosure.isOpen}
        onClose={removeUserDisclosure.onClose}
        title="Remove user"
        content="This will remove the user from your firm. They will no longer have access to your firm’s data."
        loading={deleteUser.isPending}
        actions={{
          confirm: {
            label: 'Remove',
            color: 'error',
            onClick: () => {
              deleteUser.mutate({
                adminUserId: removeUserDisclosure.actionProps.id,
              });
            },
          },
        }}
      />
      <ConfirmDialog
        open={resetMfaDisclosure.isOpen}
        onClose={resetMfaDisclosure.onClose}
        title="Reset MFA"
        content={
          <Typography variant="body2">
            Only use this if a user can no longer access the MFA device they
            registered with. They will be asked to set up a new MFA device when
            they next log in. Any passkeys they have on their devices will
            remain, and can still be used to log in.
          </Typography>
        }
        loading={resetMfa.isPending}
        actions={{
          confirm: {
            label: 'Reset MFA',
            color: 'error',
            onClick: () => {
              resetMfa.mutate({
                adminUserId: resetMfaDisclosure.actionProps.id,
              });
            },
          },
        }}
      />
      <EditUserDrawer
        open={!!editUserDrawerUserId}
        onClose={closeEditUserDrawer}
        userId={editUserDrawerUserId}
      />
      <AddUserDrawer
        open={isAddUserDrawerOpen}
        onClose={closeAddUserDrawer}
        onUserCreated={(userId) => {
          closeAddUserDrawer();
          openEditUserDrawer(userId);
        }}
      />
    </Column>
  );
};

export default Users;
